import { useEffect } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useToast } from '@/ui';
import {
  Text,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Input,
  Box,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  CloseButton,
  Alert,
  AlertTitle,
  Icon,
  type ModalProps,
} from '@/ui';
import { getErrorMessage } from '@/utils/errors';
import { useScheduleContext } from '../../contexts';
import { HeaderItem } from './components/HeaderItem';
import { LoadingState } from './components/LoadingState';
import { useGetScheduleHeaderImages } from './hooks/useGetScheduleHeaderImages';
import { useKeywordSearch } from './hooks/useKeywordSearch';
import { useLazyLoadImages } from './hooks/useLazyLoadImages';
import { useScrollTop } from './hooks/useScrollTop';
import { useUpdateScheduleHeader } from './hooks/useUpdateScheduleHeader';
import { useScheduleHeaderModal } from './store';
import type { HeaderImage } from './types';

type Props = Pick<ModalProps, 'onCloseComplete'>;

export const ScheduleHeaderModal = ({ onCloseComplete }: Props) => {
  const { schedule } = useScheduleContext();
  const { isOpen, close } = useScheduleHeaderModal();
  const { data, isLoading, error } = useGetScheduleHeaderImages();
  const { setContainer, lazyload } = useLazyLoadImages();
  const { matches, keyword, setKeyword, clear } = useKeywordSearch(data);
  const { updateScheduleHeader } = useUpdateScheduleHeader();
  const { scrollTop, onScroll } = useScrollTop();
  const { trackEvent } = useAnalytics();
  const toast = useToast();

  // load the initial set of visible headers and each time
  // the search result set changes
  useEffect(() => {
    lazyload();
  }, [matches, lazyload]);

  const handleSelect = (header: HeaderImage) => {
    updateScheduleHeader(header, {
      onError: () => toast.error('Error updating banner image'),
    });
    trackEvent('schedule:click change header', {
      headerId: header.id,
    });
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      variant="primary"
      onClose={close}
      onCloseComplete={onCloseComplete}
    >
      <ModalOverlay />

      <ModalContent minW={{ base: '100vw', md: '700px' }}>
        <ModalHeader gap="2">
          <Text pos="relative" top="1px">
            🌄
          </Text>
          Change banner image
          <Button colorScheme="dark" ml="auto" size="sm" onClick={close}>
            Done
          </Button>
        </ModalHeader>

        <ModalBody
          display="flex"
          flex="1"
          flexDir="column"
          maxH={{ base: 'auto', md: '600px' }}
          minH={{ base: 'auto', md: '600px' }}
          overflowY="auto"
          p="0"
          ref={matches.length ? setContainer : undefined}
          onScroll={onScroll}
        >
          <Box
            bg="white"
            p="6"
            pos="sticky"
            shadow={scrollTop > 0 ? 'md' : undefined}
            top="0"
            transition="all 0.2s ease-in-out"
            zIndex="2"
          >
            <InputGroup size="lg">
              <InputLeftElement>
                <Icon icon="Search" opacity="0.5" />
              </InputLeftElement>
              <Input
                fontSize="md"
                placeholder='Try "Soccer", or "Chess club"'
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              />
              {keyword.length > 0 && (
                <InputRightElement>
                  <CloseButton onClick={clear} />
                </InputRightElement>
              )}
            </InputGroup>
          </Box>

          {isLoading ? (
            <LoadingState />
          ) : error ? (
            <Alert status="error">
              <AlertTitle>{getErrorMessage(error)}</AlertTitle>
            </Alert>
          ) : keyword && !matches.length ? (
            <Text textAlign="center">
              No banners found for this search term
            </Text>
          ) : (
            <Flex direction="column" gap="3" px="6">
              {matches.map((header) => (
                <HeaderItem
                  header={header}
                  isSelected={schedule?.header?.id === header.id}
                  key={header.id}
                  onClick={() => handleSelect(header)}
                />
              ))}
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
